import React, { FC, useState, useEffect } from "react";
import firebase from "firebase/app";
import moment from "moment";
import { InformationCircleIcon } from "@heroicons/react/outline";
import {
  StripeCardElementChangeEvent,
  StripeCardElement,
} from "@stripe/stripe-js";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { Link, useHistory } from "react-router-dom";
import { StyledCourseOnlyCheckout, subscriptionCartItem } from ".";
import {
  PageLayout,
  Button,
  StripeInput,
  LabelWrapper,
  Checkbox,
  Spinner,
} from "../../System";
import { FormMessage } from "../../System/Form";
import useCart from "../../../redux/shopping-cart/useCart";
import { CartItem, PaymentPlanSelect } from "../../Cart";
import { EmptyState } from "../../EmptyState";
import { CartItemType } from "../../../types";
import { useNotifications } from "../../Notifications";
import { useAccountSubscription } from "../../Account";
import ROUTES from "../../utils/routes";

const CourseOnlyCheckout: FC = () => {
  const {
    total,
    cartItems,
    paymentError,
    discount,
    paymentLoading,
    actions: { processPayment, updateCartItemPricing, removeFromCart },
  } = useCart();
  const {
    actions: { loadSubscriptionInfo, upgradeUserSubscription },
  } = useAccountSubscription();
  const stripe = useStripe();
  const elements = useElements();

  const { addNotification } = useNotifications();

  const getPaymentInfo = firebase.functions().httpsCallable("getPaymentInfo");
  const attachPaymentMethod = firebase
    .functions()
    .httpsCallable("attachPaymentMethod");

  const [items, setItems] = useState<CartItemType[]>([]);
  const [card, setCard] = useState<StripeCardElementChangeEvent>();

  const [confirmed, setConfirmed] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [checkedPaymentMethod, setCheckedPaymentMethod] = useState(false);
  const [loading, setLoading] = useState(false);

  const canContinue = () => {
    if (hasPaymentMethod) {
      return true;
    }

    if (!confirmed) {
      return false;
    }

    if (!card) {
      return false;
    }

    if (card.error || !card.complete) {
      return false;
    }

    return true;
  };

  const updatePaymentSchedule = (e: any, courseId: string) => {
    const stripeId = e.target.value;
    updateCartItemPricing(courseId, stripeId);
  };

  const formatCartItems = () => {
    const onlyCourses = cartItems.filter((item) => item.type === "course");
    setItems([...onlyCourses, subscriptionCartItem]);
  };

  const checkPaymentMethod = async () => {
    const { data } = await getPaymentInfo({});
    setHasPaymentMethod(!!data);
    setCheckedPaymentMethod(true);
  };

  const attachCard = async () => {
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
    });

    if (error) {
      throw error.message;
    }

    await attachPaymentMethod({ paymentMethodId: paymentMethod?.id });
  };

  const handlePurchase = async () => {
    try {
      setLoading(true);

      if (!hasPaymentMethod) {
        await attachCard();
      }

      const res = await processPayment();
      if (!res?.success) {
        return;
      }

      await upgradeUserSubscription("Month", "trial-course-purchase");

      addNotification({
        message: "You have successfully subscribed!",
        type: "success",
        interval: 5000,
      });
    } catch (err) {
      addNotification({
        message: err.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    formatCartItems();
  }, [cartItems]);

  useEffect(() => {
    checkPaymentMethod();
  }, []);

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  if (!checkedPaymentMethod) {
    return <Spinner isBlock />;
  }

  return (
    <StyledCourseOnlyCheckout>
      <PageLayout maxWidth={700} pageTitle="Checkout">
        {cartItems.length > 0 && (
          <div className="subscriber-disclaimer">
            <InformationCircleIcon />
            <span>
              <Link to={ROUTES.user.settings.plan}>Become a subscriber</Link> to
              receive $98 off of your course purchase
            </span>
          </div>
        )}

        {cartItems.length === 0 && (
          <EmptyState
            title="Your cart is empty."
            suggestion="Add a course to your cart to check out."
          />
        )}

        {paymentError && <FormMessage message={paymentError} type="error" />}

        <div className="cart-area">
          {cartItems.length > 0 && (
            <>
              <ul className="cart-items">
                {items.map((item, index) => {
                  return (
                    <CartItem
                      item={item}
                      isReceipt={false}
                      removeFromCart={removeFromCart}
                      paymentPlanSelect={
                        item.type === "course" && (
                          <PaymentPlanSelect
                            courseId={item.itemData._id}
                            onChange={(e) =>
                              updatePaymentSchedule(e, item.itemData._id)
                            }
                          />
                        )
                      }
                      key={index}
                    />
                  );
                })}
              </ul>

              {!hasPaymentMethod && (
                <div className="payment-method">
                  <h2>Payment</h2>
                  <LabelWrapper
                    label="Card Information"
                    errorMessage={card?.error && card?.error.message}
                  >
                    <StripeInput onChange={(card) => setCard(card)} />
                  </LabelWrapper>
                  <Checkbox
                    name="agreeTerms"
                    onChange={(box) => setConfirmed(box.target.checked)}
                    label={
                      <>
                        I agree to the{" "}
                        <a
                          href={ROUTES.external.termsOfServiceUrl}
                          target="_blank"
                          referrerPolicy="no-referrer"
                          rel="noreferrer"
                        >
                          terms of service
                        </a>{" "}
                        and{" "}
                        <a
                          href={ROUTES.external.cancellationPolicyUrl}
                          target="_blank"
                          referrerPolicy="no-referrer"
                          rel="noreferrer"
                        >
                          cancellation policy
                        </a>
                        . I acknowledge that upon purchase of S.O.S. I'm
                        subscribing to Social Curator and will be charged $49 on{" "}
                        {moment()
                          .add(2, "months")
                          .format("MM/DD/YY")}{" "}
                        unless I cancel my subscription, which can be canceled
                        at any time without losing access to course content.
                      </>
                    }
                  />
                </div>
              )}

              <div className="checkout-actions">
                {!!total && (
                  <div className="cart-total">
                    {!!discount && (
                      <div className="discount-info">
                        <span>
                          <s>${(total + discount).toFixed(2)}</s>
                        </span>
                        <span>-${discount.toFixed(2)}</span>
                      </div>
                    )}
                    <span>
                      Total <b>${total.toFixed(2)}</b>
                    </span>
                  </div>
                )}
                {!total && <span />}
                <Button
                  isDisabled={loading || paymentLoading || !canContinue()}
                  isLoading={paymentLoading || loading}
                  onClick={() => handlePurchase()}
                >
                  Checkout
                </Button>
              </div>
            </>
          )}
        </div>
      </PageLayout>
    </StyledCourseOnlyCheckout>
  );
};

export default CourseOnlyCheckout;
